import { Link } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { TurfTalk, AuthExpiredError } from "@/api/client";
import { GameCard, PageHeader, Empty, Skeleton } from "@/components/ui-kit";
import { isLiveStatus, combineHomeGames } from "@/lib/gameStatus";
import { gamesQueryParams, mergeGamesPage, makeRequestSequencer } from "@/lib/gamesFeed";

const LEAGUES = ["ALL", "NFL", "NCAA"];
// "current" is the default: a useful current view (live + upcoming + recent
// finals from the canonical /home feed) — never the unfiltered oldest rows.
const STATUSES = [
  { key: "current", label: "Now" },
  { key: "live", label: "Live" },
  { key: "scheduled", label: "Scheduled" },
  { key: "final", label: "Final" },
  { key: "all", label: "All" },
];
const PAGE = 50;

export default function GamesPage() {
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState("ALL");
  const [status, setStatus] = useState("current");
  const [skip, setSkip] = useState(0);
  const seq = useRef(makeRequestSequencer());

  const isCurrent = status === "current";

  // Fetch. "current" composes the canonical /home buckets (live → upcoming →
  // recent finals) and filters by league client-side (bounded, no pagination).
  // Every other filter is server-side (status_filter) with skip/limit paging.
  useEffect(() => {
    const id = seq.current.next();
    setLoading(true);
    if (isCurrent) {
      TurfTalk.home()
        .then((h) => {
          if (!seq.current.isCurrent(id)) return;
          let list = combineHomeGames(h);
          if (league !== "ALL") list = list.filter((g) => g.league === league);
          setGames(list); setTotal(list.length);
        })
        .catch((e) => { if (seq.current.isCurrent(id) && !(e instanceof AuthExpiredError)) { setGames([]); setTotal(0); } })
        .finally(() => { if (seq.current.isCurrent(id)) setLoading(false); });
      return;
    }
    TurfTalk.games(gamesQueryParams({ league, status, limit: PAGE, skip }))
      .then((r) => {
        if (!seq.current.isCurrent(id)) return; // stale response — ignore
        setTotal(r.total ?? (r.games?.length ?? 0));
        setGames((prev) => mergeGamesPage(prev, r.games, skip));
      })
      .catch((e) => {
        if (seq.current.isCurrent(id) && !(e instanceof AuthExpiredError) && skip === 0) { setGames([]); setTotal(0); }
      })
      .finally(() => { if (seq.current.isCurrent(id)) setLoading(false); });
  }, [league, status, skip, isCurrent]);

  // Live auto-refresh of the current first page (only when a shown game is live).
  const anyLive = useMemo(() => games.some((g) => isLiveStatus(g.status)), [games]);
  useEffect(() => {
    if (!anyLive || skip !== 0) return;
    const iv = setInterval(() => {
      const id = seq.current.next();
      if (isCurrent) {
        TurfTalk.home().then((h) => { if (seq.current.isCurrent(id)) { let l = combineHomeGames(h); if (league !== "ALL") l = l.filter((g) => g.league === league); setGames(l); setTotal(l.length); } }).catch(() => {});
      } else {
        TurfTalk.games(gamesQueryParams({ league, status, limit: PAGE, skip: 0 }))
          .then((r) => { if (seq.current.isCurrent(id)) { setTotal(r.total ?? 0); setGames(r.games || []); } })
          .catch(() => {});
      }
    }, 45000);
    return () => clearInterval(iv);
  }, [anyLive, skip, league, status, isCurrent]);

  const changeLeague = (l) => { if (l === league) return; setLeague(l); setSkip(0); setGames([]); };
  const changeStatus = (s) => { if (s === status) return; setStatus(s); setSkip(0); setGames([]); };

  const weeks = useMemo(() => {
    const grouped = {};
    for (const g of games) { (grouped[g.week] ??= []).push(g); }
    return Object.entries(grouped).sort((a, b) => b[0] - a[0]);
  }, [games]);

  const hasMore = !isCurrent && games.length < total;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
      <div className="mb-4"><Link to="/rehearsal" className="inline-block border border-white/30 rounded-lg px-4 py-3 text-white">Set up a private AI rehearsal</Link></div>
      <PageHeader title="Games" subtitle="NFL & NCAA discovery — live now, upcoming, and recent finals." />
      <div className="flex flex-wrap items-center gap-2 mb-6 sticky top-0 md:top-12 z-20 py-2 bg-[#10151D]/90 backdrop-blur">
        {LEAGUES.map((l) => (
          <button key={l} onClick={() => changeLeague(l)} data-testid={`filter-league-${l}`}
            className={`px-4 h-9 rounded-full text-sm font-display tracking-wide transition-colors ${league === l ? "bg-[#2F7D32] text-white" : "tt-panel text-white/60 hover:text-white"}`}>
            {l}
          </button>
        ))}
        <span className="w-px h-6 bg-[#2A3544] mx-1" />
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => changeStatus(s.key)} data-testid={`filter-status-${s.key}`}
            className={`px-3 h-9 rounded-full text-xs capitalize transition-colors ${status === s.key ? "bg-[#24362A] text-white border border-[#2F7D32]" : "text-white/50 hover:text-white"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {loading && games.length === 0 ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
      ) : games.length === 0 ? (
        <Empty title={isCurrent ? "No current games" : "No games match"} detail={isCurrent ? "No live, upcoming, or recent games in the feed right now." : "Try a different league or status filter."} />
      ) : (
        <>
          {weeks.map(([wk, list]) => (
            <section key={wk} className="mb-8">
              <div className="font-display tracking-widest text-xs text-white/40 mb-3">WEEK {wk}</div>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{list.map((g) => <GameCard key={g.id} game={g} />)}</div>
            </section>
          ))}
          {hasMore && (
            <div className="flex justify-center mt-2">
              <button onClick={() => setSkip((s) => s + PAGE)} disabled={loading} data-testid="games-load-more"
                className="px-5 h-10 rounded-full text-sm font-display tracking-wide tt-panel text-white/80 hover:text-white disabled:opacity-50 transition-colors">
                {loading ? "Loading…" : `Load more (${games.length}/${total})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
